import { useLang } from "@/lib/i18n";
import { IMG, PARTS } from "@/lib/images";
import { FadeIn, SectionHead } from "@/components/Reveal";
import { PrimaryBtn } from "@/components/Buttons";

const CARD_IMAGES = [PARTS.cracked2, PARTS.motherboard, IMG.gloves];

export default function Activities() {
  const { t } = useLang();
  const c = t.activities;

  return (
    <div>
      <section className="relative overflow-hidden pb-8 pt-16 lg:pt-24" data-testid="activities-hero">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_55%_at_75%_0%,rgba(14,124,90,0.08),transparent_70%)]" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={14}>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-brand/40" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-brand">
                {c.eyebrow}
              </span>
            </div>
            <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[1.15] text-ink sm:text-5xl lg:text-6xl">
              {c.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink2 sm:text-lg">{c.lead}</p>
          </FadeIn>
        </div>
      </section>

      <section className="py-12 lg:py-20" data-testid="activities-list">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          {c.cards.map((card, i) => (
            <FadeIn key={i} delay={0.05}>
              <div className="grid items-center gap-8 border-t border-line py-12 lg:grid-cols-12 lg:py-16">
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="relative">
                    <span
                      aria-hidden
                      className={`absolute -top-4 hidden h-full w-full rounded-none border-2 border-brand/25 sm:block ${
                        i % 2 === 1 ? "-left-4" : "-right-4"
                      }`}
                    />
                    <div className="relative overflow-hidden rounded-none shadow-card">
                      <img
                        src={CARD_IMAGES[i]}
                        alt={card.title}
                        className="h-64 w-full object-cover transition-transform duration-700 hover:scale-[1.03] lg:h-80"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </div>
                <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1 lg:pr-8" : "lg:pl-8"}`}>
                  <span className="text-sm font-bold text-brand">{card.num}</span>
                  <h2 className="mt-3 font-display text-3xl font-semibold text-ink sm:text-4xl">
                    {card.title}
                  </h2>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-ink2">{card.desc}</p>
                  <div className="mt-7 flex flex-wrap gap-2.5">
                    {card.items.map((it) => (
                      <span
                        key={it}
                        className="rounded-none border border-line bg-white px-4 py-1.5 text-xs font-medium text-ink2 transition-colors duration-200 hover:border-brand/50 hover:text-brand"
                      >
                        {it}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      <section className="border-t border-line bg-white py-20" data-testid="activities-cta">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-10 px-5 sm:px-8 lg:flex-row lg:items-center">
          <SectionHead eyebrow={c.eyebrow} title={c.ctaTitle} />
          <FadeIn delay={0.15}>
            <PrimaryBtn to="/contact" testid="activities-cta-lot">
              {c.ctaButton}
            </PrimaryBtn>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
